import React, { useState } from "react";
import { Avatar, Badge, Popover, message } from "antd";
import { Pin, Megaphone, User, Users, MoreHorizontal, PinOff, VolumeX, Bell, BellOff } from "lucide-react";
import { ChatItem as ChatItemType } from "../../types/chat";
import { getAvatarText, formatRelativeTime } from "../../utils";
import { usePinRoom, useMuteRoom } from "../../hooks/mutations/chat";
import styles from "./ChatItem.module.scss";

interface ChatItemProps {
  room: ChatItemType;
  active?: boolean;
  onClick?: () => void;
}

const ChatItem: React.FC<ChatItemProps> = ({ room, active = false, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const pinRoomMutation = usePinRoom();
  const muteRoomMutation = useMuteRoom();

  const parseLastMessage = (message: string | null) => {
    if (!message) return { sender: "", content: room.Description || "" };

    const colonIndex = message.indexOf(":");
    if (colonIndex > 0) {
      return {
        sender: message.substring(0, colonIndex).trim(),
        content: message.substring(colonIndex + 1).trim(),
      };
    }
    return { sender: "", content: message };
  };

  // Xác định icon theo loại phòng
  const getRoomTypeIcon = (type?: string) => {
    switch (type) {
      case "C": // Channel/Kênh thông báo
        return <Megaphone size={12} className={styles.roomTypeIcon} />;
      case "U": // User/Cá nhân
        return <User size={12} className={styles.roomTypeIcon} />;
      case "D": // Department/Phòng ban
      case "P": // Public Group/Nhóm công khai
      case "G": // Created Group/Nhóm
      default:
        return <Users size={12} className={styles.roomTypeIcon} />;
    }
  };

  const { sender, content } = parseLastMessage(room.LastMessage);
  const unreadCount = 0; // Assuming unread count is not available in the provided ChatItemType

  const handlePinToggle = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering room click
    setPopoverOpen(false);

    pinRoomMutation.mutate(
      { roomId: room.ID, pin: !room.Pin },
      {
        onSuccess: () => {
          message.success(room.Pin ? "Đã bỏ ghim phòng chat" : "Đã ghim phòng chat");
        },
        onError: () => {
          message.error("Có lỗi xảy ra, vui lòng thử lại");
        },
      },
    );
  };

  const handleMuteToggle = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering room click
    setPopoverOpen(false);

    muteRoomMutation.mutate(room.ID, {
      onSuccess: () => {
        message.success(room.IsMuted ? "Đã bật thông báo" : "Đã tắt thông báo");
      },
      onError: () => {
        message.error("Có lỗi xảy ra, vui lòng thử lại");
      },
    });
  };

  const popoverContent = (
    <div className={styles.popoverMenu}>
      <div className={styles.menuItem} onClick={handlePinToggle}>
        {room.Pin ? <PinOff size={16} /> : <Pin size={16} />}
        <span>{room.Pin ? "Bỏ ghim phòng chat" : "Ghim phòng chat"}</span>
      </div>
      <div className={styles.menuItem} onClick={handleMuteToggle}>
        {room.IsMuted ? <Bell size={16} /> : <BellOff size={16} />}
        <span>{room.IsMuted ? "Bật thông báo" : "Tắt thông báo"}</span>
      </div>
    </div>
  );

  return (
    <div
      className={`${styles.chatItem} ${active ? styles.active : ""}`}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setPopoverOpen(false);
      }}
    >
      <div className={styles.avatarWrapper}>
        <Avatar size={48} src={room.AvatarUrl} className={styles.avatar}>
          {!room.AvatarUrl && getAvatarText(room.RoomName)}
        </Avatar>
        <span className={styles.roomTypeIconBadge}>{getRoomTypeIcon(room.Type)}</span>
      </div>

      <div className={styles.chatInfo}>
        <div className={styles.chatHeader}>
          <div className={styles.nameWrapper}>
            <span className={styles.chatName}>{room.RoomName}</span>
            {room.Pin && <Pin size={14} className={styles.pinIcon} />}
          </div>
          <div className={styles.timeWrapper}>
            {room.IsMuted && <VolumeX size={14} className={styles.muteIcon} />}
            <span className={styles.chatTime}>{formatRelativeTime(room.Modified)}</span>
            {isHovered && (
              <Popover
                content={popoverContent}
                trigger="click"
                open={popoverOpen}
                onOpenChange={setPopoverOpen}
                placement="bottomRight"
                overlayClassName="pin-room-popover"
              >
                <div
                  className={styles.moreButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPopoverOpen(!popoverOpen);
                  }}
                >
                  <MoreHorizontal size={16} />
                </div>
              </Popover>
            )}
          </div>
        </div>

        <div className={styles.chatPreview}>
          {sender && <span className={styles.sender}>{sender}:</span>}
          <span className={styles.message}>{content}</span>
          {unreadCount > 0 && <Badge count={unreadCount} className={styles.unreadBadge} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ChatItem);
